// @flow strict\
import React from "react";
import { imgSrc } from "~common/utils";
import { playersMap } from "~robin-hood/utils";
import type { Player } from "~robin-hood/utils";
import type {
  Equipo,
  RegisteredStore,
  LocalCasillas,
  Casillas,
} from "~robin-hood/redux/store";
import {
  equipoWithPrueba,
  localStoreWithPrueba,
} from "~robin-hood/redux/store";
import classnames from "~common/classnames";
import { dots } from "cli-spinners";
import styles from "./PruebaCasillas.module.css";
import window from "~robin-hood/animations/window.module.css";

const CASILLAS_SECRET: Casillas = "2596";

const CASILLAS_IMG_PLAYER = playersMap([
  imgSrc("/images/robin-hood/prueba-casillas/CASILLAS1.png"),
  imgSrc("/images/robin-hood/prueba-casillas/CASILLAS2.png"),
  imgSrc("/images/robin-hood/prueba-casillas/CASILLAS3.png"),
  imgSrc("/images/robin-hood/prueba-casillas/CASILLAS4.png"),
  imgSrc("/images/robin-hood/prueba-casillas/CASILLAS5.png"),
  imgSrc("/images/robin-hood/prueba-casillas/CASILLAS6.png"),
]);

type Props = {
  player: Player,
  store: RegisteredStore,
  updateLocalStore: (RegisteredStore) => void,
  equipo: Equipo,
  updateEquipo: (equipo: Equipo) => void,
};

export class PruebaCasillas extends React.PureComponent<Props> {
  render(): React$Node {
    const { player } = this.props;
    return (
      <div
        className={classnames(styles.pruebaCasillasWrapper, window.scaleInBl)}
      >
        <div className={classnames(styles.pruebaCasillas)}>
          <img src={CASILLAS_IMG_PLAYER.get(player)} />
        </div>
        <Solucion
          player={player}
          store={this.props.store}
          updateLocalStore={this.props.updateLocalStore}
          equipo={this.props.equipo}
          updateEquipo={this.props.updateEquipo}
        />
      </div>
    );
  }
}

const DEFAULT_CASILLAS = "";

function valueFromLocal(store: RegisteredStore, player: Player): Casillas {
  const casillas = store.local?.casillas;
  return (
    (casillas && casillas[player] && casillas[player].value) || DEFAULT_CASILLAS
  );
}

function valueFromEquipo(equipo: Equipo) {
  return equipo.pruebas?.casillas?.value || DEFAULT_CASILLAS;
}
function correctFromEquipo(equipo: Equipo) {
  return equipo.pruebas?.casillas?.correct || false;
}
type SolucionProps = {
  player: Player,
  store: RegisteredStore,
  updateLocalStore: (RegisteredStore) => void,
  equipo: Equipo,
  updateEquipo: (Equipo) => void,
};

const SPINNER = dots;
const RESET = 2000;

function next(color: ?number): number {
  return ((color ?? 0) + 1) % SPINNER.frames.length;
}
class Solucion extends React.PureComponent<
  SolucionProps,
  { checking?: number, checked: boolean, tick: number }
> {
  _mounted: boolean;
  _timeout: ?TimeoutID;
  state = {
    checking: undefined,
    checked: !!correctFromEquipo(this.props.equipo),
    tick: 0,
  };

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = undefined;
    }
  }

  _doCheck = () => {
    const { store, equipo, updateEquipo, player } = this.props;
    const value = valueFromLocal(store, player);
    const isCorrect = correctFromEquipo(equipo);
    const correct = CASILLAS_SECRET === value;
    if (correct && correct !== isCorrect) {
      const updated = equipoWithPrueba(equipo, {
        casillas: { value, correct },
      });
      updateEquipo(updated);
    }
  };

  _tick = () => {
    if (!this._mounted) {
      clearTimeout(this._timeout);
      this._timeout = undefined;
      return;
    }
    this.setState(
      (state) => ({
        tick: state.tick + 1,
        checking: next(state.checking),
      }),
      () => {
        if (!this._mounted || this._timeout) return;
        const TICKS = SPINNER.frames.length * 3;
        if (this.state.tick === TICKS - 1) {
          // Check early so result is ready
          this._doCheck();
        }
        if (this.state.tick < TICKS) {
          this._timeout = setTimeout(() => {
            this._timeout = undefined;
            this._tick();
          }, SPINNER.interval);
        } else {
          // Finished
          this.setState(
            {
              tick: 0,
              checking: undefined,
              checked: true,
            },
            () => {
              this._timeout = setTimeout(() => {
                this.setState({ checked: false });
                this._timeout = undefined;
              }, RESET);
            }
          );
        }
      }
    );
  };

  _doChange = (event: InputEvent) => {
    const { store, updateLocalStore, player } = this.props;
    const localValue = valueFromLocal(store, player);
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }
    const value = target.value;
    if (value === localValue) {
      return;
    }
    updateLocalStore(updateLocalWithCasillas(store, player, { value }));
  };

  _doStartCheck = () => {
    const { equipo } = this.props;
    const correct = correctFromEquipo(equipo);
    const { checking, checked } = this.state;
    if (correct || checking || this._timeout) return;
    if (checked) {
      this.setState({ checked: false });
    } else {
      this._tick();
    }
  };

  render(): React$Node {
    const { equipo, store, player } = this.props;
    const correct = correctFromEquipo(equipo);
    const value = correct
      ? valueFromEquipo(equipo)
      : valueFromLocal(store, player);
    const { checking, checked } = this.state;
    const content: React$Node = correct ? (
      ""
    ) : checking != null ? (
      SPINNER.frames[checking]
    ) : !checked ? (
      <span className="fa fa-question" />
    ) : (
      ""
    );

    return (
      <div className={styles.solucionWrapper}>
        <form
          className={styles.solucion}
          onSubmit={(e) => {
            e.preventDefault();
            this._doStartCheck();
          }}
        >
          <input
            type="text"
            autoFocus
            disabled={correct}
            defaultValue={value}
            onChange={this._doChange}
          />
          <div
            className={classnames(
              styles.solucionCheck,
              correct
                ? styles.solucionCorrect
                : checking != null
                ? styles.solucionChecking
                : checked && styles.solucionIncorrect
            )}
            onClick={this._doStartCheck}
          >
            {content}
          </div>
        </form>
      </div>
    );
  }
}

function updateLocalWithCasillas(
  store: RegisteredStore,
  player: Player,
  value: LocalCasillas
): RegisteredStore {
  return localStoreWithPrueba(store, "casillas", {
    [(player: string)]: value,
  });
}
